/**
 * Filename normalization and item metadata row lookup (config + runtime).
 */

/** @typedef {{ trimWhitespace?: boolean, caseInsensitive?: boolean }} MatchOptions */

const DEFAULT_MATCH = { trimWhitespace: true, caseInsensitive: false };

/**
 * @param {string} name
 * @param {MatchOptions} [opts]
 */
export function normalizeFilename(name, opts = {}) {
    const o = { ...DEFAULT_MATCH, ...opts };
    let s = String(name ?? '');
    if (o.trimWhitespace) s = s.trim();
    if (o.caseInsensitive) s = s.toLowerCase();
    return s;
}

/**
 * Find first row in itemMetadata.rows whose filename key matches item name.
 *
 * @param {Array<Record<string, string>>} rows
 * @param {string} itemName - Girder item name
 * @param {MatchOptions} [matchOpts]
 * @returns {Record<string, string>|null}
 */
export function findItemRowForItemName(rows, itemName, matchOpts = {}) {
    if (!Array.isArray(rows) || rows.length === 0) return null;
    const key = normalizeFilename(itemName, matchOpts);
    for (const row of rows) {
        if (!row || typeof row !== 'object') continue;
        const fn = row.filename ?? '';
        if (normalizeFilename(fn, matchOpts) === key) {
            return row;
        }
    }
    return null;
}

/** @deprecated Use findItemRowForItemName */
export const findSlideRowForItemName = findItemRowForItemName;

/**
 * @param {{ name?: string }[]} projectItems
 * @param {Array<Record<string, string>>} rows
 * @param {MatchOptions} matchOpts
 */
export function computeItemMetadataCoverage(projectItems, rows, matchOpts = {}) {
    const items = Array.isArray(projectItems) ? projectItems : [];
    const rowList = Array.isArray(rows) ? rows : [];

    const nameCounts = {};
    items.forEach((it) => {
        const n = String(it?.name ?? '');
        nameCounts[n] = (nameCounts[n] ?? 0) + 1;
    });
    const duplicateItemNames = Object.keys(nameCounts).filter((n) => nameCounts[n] > 1);

    const itemKeys = new Set(
        items.map((it) => normalizeFilename(it?.name, matchOpts)).filter(Boolean),
    );

    const rowKeys = [];
    const keyToDisplay = new Map();
    rowList.forEach((row) => {
        const raw = row?.filename ?? '';
        const k = normalizeFilename(raw, matchOpts);
        if (k) {
            rowKeys.push(k);
            if (!keyToDisplay.has(k)) keyToDisplay.set(k, String(raw).trim() || k);
        }
    });

    const rowKeySet = new Set(rowKeys);

    const unmatchedItemNames = [];
    items.forEach((it) => {
        const k = normalizeFilename(it?.name, matchOpts);
        if (k && !rowKeySet.has(k)) {
            unmatchedItemNames.push(String(it.name));
        }
    });

    const orphanKeys = [...rowKeySet].filter((k) => !itemKeys.has(k));
    const orphanFilenames = orphanKeys.map((k) => keyToDisplay.get(k) ?? k);

    const matchedCount = items.filter((it) => {
        const k = normalizeFilename(it?.name, matchOpts);
        return k && rowKeySet.has(k);
    }).length;

    return {
        unmatchedItemNames,
        unmatchedSlideNames: unmatchedItemNames,
        orphanFilenames,
        duplicateItemNames,
        matchedCount,
    };
}

/** @deprecated Use computeItemMetadataCoverage */
export const computeSlideInfoCoverage = computeItemMetadataCoverage;
